await customElements.whenDefined("rr-product-inquiry");
const InquiryBase = customElements.get("rr-product-inquiry");

class RRPageInquiry extends InquiryBase {
  connectedCallback() {
    super.connectedCallback();
    this.open();
    this.source = this.querySelector("[data-page-source]");
    this.detail = this.querySelector("[data-page-source-detail]");
    this.detailLabel = this.querySelector("[data-page-detail-label]");
    this.detailReveal = this.querySelector("[data-page-source-reveal]");
    this.message = this.querySelector("[data-page-message]");
    this.service = this.querySelector("[data-page-service]");
    this.messageReveal = this.querySelector("[data-page-message-reveal]");
    this.detailValues = new Map();
    this.activeSource = "";
    const signal = this.abortController.signal;
    this.detail?.addEventListener("input", () => this.syncOptionalLabels(), { signal });
    this.detail?.addEventListener("change", () => this.syncOptionalLabels(), { signal });
    this.source?.addEventListener("change", () => this.syncSource(), { signal });
    this.service?.addEventListener("change", () => this.syncService(), { signal });
    this.message?.addEventListener("input", () => {
      this.resizeMessage();
      this.refreshVisibleContactError();
    }, { signal });
    this.message?.addEventListener("change", () => this.resizeMessage(), { signal });
    this.restoreDraft();
    this.restoreSearchContext();
    this.resizeMessage();
    const conciergeLinked = this.restoreConciergeContext();
    this.setupConciergeServiceLinks();
    this.setupSourceDropdown();
    this.setupDropdown(this.service);
    this.setupConciergeHandAnchor();
    this.syncService();
    this.messageObserver?.disconnect();
    if (this.message && "ResizeObserver" in window) {
      let previousWidth = this.message.getBoundingClientRect().width;
      this.messageObserver = new ResizeObserver(() => {
        const width = this.message?.getBoundingClientRect().width;
        if (width === previousWidth) return;
        previousWidth = width;
        this.resizeMessage();
      });
      this.messageObserver.observe(this.message);
    }
    document.fonts?.ready.then(() => {
      if (!signal.aborted && this.isConnected) this.resizeMessage();
    });
    this.syncSource();
    this.syncNewsletterOption();
    if (conciergeLinked) {
      const instant = new URLSearchParams(location.search).get("rr_scroll") === "instant";
      this.scrollToSuccessAnchor(!instant, 2000, instant);
    }
    if (this.querySelector("[data-inquiry-success]")) {
      try { this.pendingCustomerStorage?.removeItem(this.draftKey); } catch {}
      this.scrollToSuccessAnchor();
    }
  }

  setupConciergeHandAnchor() {
    const grid = this.closest(".rr-concierge-contact");
    const image = grid?.querySelector(".rr-concierge-contact__image");
    const column = grid?.querySelector(":scope > .container");
    if (!image || !column) return;
    const signal = this.abortController.signal;
    const measure = () => {
      if (signal.aborted || !this.isConnected || innerWidth < 1000) return;
      // Use the collapsed form's height, excluding optional follow-up rows.
      // Form changes never trigger this measurement or recenter the hand.
      const extra = [this.messageReveal, this.detailReveal].reduce((total, row) => {
        if (!row) return total;
        const css = getComputedStyle(row);
        return total + row.getBoundingClientRect().height +
          (parseFloat(css.marginTop) || 0) + (parseFloat(css.marginBottom) || 0);
      }, 0);
      const baseHeight = column.getBoundingClientRect().height - extra;
      const inset = Math.max(0, (baseHeight - image.getBoundingClientRect().height) / 2);
      image.style.setProperty("--rr-contact-hand-inset", `${inset}px`);
    };
    measure();
    window.addEventListener("resize", measure, { passive: true, signal });
    image.querySelector("img")?.addEventListener("load", measure, { signal });
    document.fonts?.ready.then(measure);
  }

  restoreConciergeContext() {
    if (!this.service || this.querySelector("[data-inquiry-success]") ||
        location.hash !== "#concierge-form") return false;
    const key = new URLSearchParams(location.search).get("rr_service");
    const option = [...this.service.options].find(item => item.dataset.serviceKey === key);
    if (!option) return false;
    this.service.value = option.value;
    return true;
  }

  setupConciergeServiceLinks() {
    if (!this.service || location.pathname !== "/pages/concierge") return;
    document.addEventListener("click", async event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.(".rr-concierge-service-action a");
      if (!link || link.target === "_blank" || this.querySelector("[data-inquiry-success]")) return;
      const key = link.closest("[data-concierge-service]").dataset.conciergeService;
      const option = [...this.service.options].find(item => item.dataset.serviceKey === key);
      if (!option) return;
      event.preventDefault();
      this.service.value = option.value;
      this.service.dispatchEvent(new Event("change", { bubbles: true }));
      history.pushState(null, "", link.href);
      if (["sourcing", "bespoke"].includes(key)) {
        // Wait for the enhanced scene before measuring a cold-cache arrival.
        await document.querySelector("rr-sketch-rings")?.ready;
        if (!this.abortController.signal.aborted && this.isConnected) this.scrollThroughSketchToForm();
      }
      else this.scrollToSuccessAnchor(true, 1000);
    }, { capture: true, signal: this.abortController.signal });
  }

  scrollThroughSketchToForm() {
    this.finishSketchScroll?.();
    cancelAnimationFrame(this.successAnchorFrame);
    cancelAnimationFrame(this.dropdownScrollFrame);
    const destination = () => this.conciergeAnchorTop();
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      window.scrollTo({ top: destination(), behavior: "instant" });
      return;
    }
    const scene = document.querySelector(".rr-sketch-rings-scene");
    const sceneStyle = scene && getComputedStyle(scene);
    const runway = parseFloat(sceneStyle?.getPropertyValue("--rr-sketch-runway")) || 0;
    const sceneTop = scene ? window.scrollY + scene.getBoundingClientRect().top - (parseFloat(sceneStyle.getPropertyValue("--rr-sketch-top")) || 0) : 0;
    const start = window.scrollY;
    const end = destination();
    const mobile = matchMedia("(max-width: 699px)").matches;
    // Start departure at sticky release, when the desktop dissolve is complete.
    const sticky = scene?.querySelector(".rr-sketch-rings-sticky");
    const releaseDistance = sticky
      ? Math.max(0, (scene.querySelector(".rr-sketch-rings-track")?.offsetHeight || scene.offsetHeight) - sticky.offsetHeight)
      : runway;
    const motionRunway = mobile ? parseFloat(sceneStyle?.getPropertyValue("--rr-sketch-motion-runway")) || runway : runway;
    const sceneExit = sceneTop + (mobile ? Math.min(releaseDistance, motionRunway * .9) : releaseDistance);
    const crossesScene = runway > 0 && start < sceneExit && end > sceneTop;
    const traverseFrom = Math.max(start, sceneTop);
    const traverseTo = Math.max(traverseFrom, Math.min(end, sceneExit));
    const phases = mobile ? this.mobileConciergeScrollPhases(start, Math.max(start, sceneTop), traverseTo, end, motionRunway, crossesScene) : crossesScene ? [
      { from: start, to: Math.max(start, sceneTop), duration: start < sceneTop ? 650 : 0 },
      { from: traverseFrom, to: traverseTo, duration: 3000 * (traverseTo - traverseFrom) / runway, linear: true },
      { from: traverseTo, to: end, duration: 850 }
    ] : [{ from: start, to: end, duration: 2000 }];
    if (crossesScene && !mobile) {
      const traverse = phases[1];
      const departure = phases[2];
      const distance = departure.to - departure.from;
      // Carry the runway velocity into departure instead of restarting from rest.
      departure.startSlope = distance > 0
        ? Math.min(3, runway / 3000 * departure.duration / distance)
        : 0;
    }
    // Block manual input without changing the root scroll container or sticky geometry.
    const lock = new AbortController();
    const anchoring = document.documentElement.style.overflowAnchor;
    if (mobile) document.documentElement.style.overflowAnchor = "none";
    const prevent = event => event.preventDefault();
    if (mobile) {
      for (const type of ["wheel", "touchstart", "pointerdown"]) window.addEventListener(type, () => this.finishSketchScroll?.(), { capture: true, passive: true, signal: lock.signal });
    } else {
      for (const type of ["wheel", "touchmove"]) window.addEventListener(type, prevent, { capture: true, passive: false, signal: lock.signal });
    }
    window.addEventListener("keydown", event => {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) {
        if (mobile) this.finishSketchScroll?.(); else event.preventDefault();
      }
      if (event.key === "Escape") this.finishSketchScroll?.();
    }, { capture: true, signal: lock.signal });
    this.finishSketchScroll = () => {
      cancelAnimationFrame(this.sketchScrollFrame);
      clearTimeout(this.sketchScrollWatchdog);
      lock.abort();
      if (mobile) document.documentElement.style.overflowAnchor = anchoring;
      this.finishSketchScroll = null;
    };
    window.addEventListener("pagehide", () => this.finishSketchScroll?.(), { once: true, signal: lock.signal });
    this.abortController.signal.addEventListener("abort", () => this.finishSketchScroll?.(), { once: true, signal: lock.signal });
    const total = phases.reduce((sum, item) => sum + item.duration, 0);
    this.sketchScrollWatchdog = setTimeout(() => this.finishSketchScroll?.(), mobile ? Math.max(10000, total * 2) : total + 1500);
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    motion.addEventListener("change", () => { this.finishSketchScroll?.(); window.scrollTo({ top: destination(), behavior: "instant" }); }, { once: true, signal: lock.signal });
    const began = performance.now();
    let motionTime = 0;
    let previousFrame = began;
    const step = now => {
      if (!this.isConnected) { this.finishSketchScroll?.(); return; }
      motionTime = mobile ? motionTime + Math.min(24, Math.max(0, now - previousFrame)) : now - began;
      previousFrame = now;
      let elapsed = motionTime;
      let phase;
      for (const item of phases) {
        phase = item;
        if (elapsed < item.duration) break;
        elapsed -= item.duration;
      }
      if (motionTime >= total) {
        window.scrollTo({ top: destination(), behavior: "instant" });
        this.finishSketchScroll?.();
        return;
      }
      const progress = Math.min(1, elapsed / phase.duration);
      const eased = phase.linear ? progress : phase.startSlope !== undefined
        ? phase.startSlope * progress + (3 - 2 * phase.startSlope - (phase.endSlope || 0)) * progress ** 2 + (phase.startSlope + (phase.endSlope || 0) - 2) * progress ** 3
        : progress * progress * progress * (progress * (progress * 6 - 15) + 10);
      window.scrollTo({ top: phase.from + (phase.to - phase.from) * eased, behavior: "instant" });
      this.sketchScrollFrame = requestAnimationFrame(step);
    };
    this.sketchScrollFrame = requestAnimationFrame(step);
  }

  mobileConciergeScrollPhases(start, entry, exit, end, runway, crossesScene) {
    const speed = runway / 1800;
    const phase = (from, to, startSpeed = 0, endSpeed = 0, minimum = 650) => {
      const distance = Math.max(0, to - from);
      const duration = distance ? Math.min(Math.max(minimum, distance / 0.55), Math.max(startSpeed, endSpeed) > 0 ? 2 * distance / Math.max(startSpeed, endSpeed) : Infinity) : 0;
      return { from, to, duration, startSlope: distance ? startSpeed * duration / distance : 0, endSlope: distance ? endSpeed * duration / distance : 0 };
    };
    if (!crossesScene) return [phase(start, end, 0, 0, 1200)];
    return [
      phase(start, entry, 0, speed),
      { from: Math.max(start, entry), to: exit, duration: speed > 0 ? Math.max(0, exit - Math.max(start, entry)) / speed : 0, linear: true },
      phase(exit, end, speed, 0, 1200)
    ];
  }

  restoreSearchContext() {
    if (!this.message || !location.hash.startsWith("#rr-search=")) return;
    let context;
    try { context = decodeURIComponent(location.hash.slice(11)).slice(0, 5000); } catch { return; }
    if (!context.trim()) return;
    if (!this.message.value.trim()) this.message.value = context;
    else if (!this.message.value.includes(context)) this.message.value += "\n\n" + context;
    // The customer reviews the message and explicitly submits the existing form.
    history.replaceState(null, "", location.pathname + location.search);
    this.message.dispatchEvent(new Event("input", { bubbles: true }));
  }

  setupSourceDropdown() {
    if (!this.source || this.sourceButton) return;
    this.sourceButton = this.setupDropdown(this.source);
  }

  setupDropdown(select) {
    if (!select || select.hidden) return;
    const signal = this.abortController.signal;
    const wrapper = document.createElement("div");
    wrapper.className = "rr-source-dropdown";
    const button = document.createElement("button");
    button.type = "button";
    button.className = "rr-source-dropdown__button";
    button.id = `${select.id}-button`;
    button.setAttribute("role", "combobox");
    button.setAttribute("aria-haspopup", "listbox");
    button.setAttribute("aria-expanded", "false");
    const label = this.querySelector(`label[for="${select.id}"]`);
    if (label) {
      label.id ||= `${select.id}-label`;
      label.htmlFor = button.id;
      button.setAttribute("aria-labelledby", label.id);
    }
    const caption = document.createElement("span");
    const arrow = document.createElement("span");
    arrow.className = "rr-source-dropdown__arrow";
    arrow.setAttribute("aria-hidden", "true");
    button.append(caption, arrow);
    const list = document.createElement("div");
    list.className = "rr-source-dropdown__list";
    list.id = `${select.id}-list`;
    list.setAttribute("role", "listbox");
    if (label) list.setAttribute("aria-labelledby", label.id);
    button.setAttribute("aria-controls", list.id);
    const items = [...select.options].map((option, index) => {
      const item = document.createElement("div");
      item.id = `${list.id}-${index}`;
      item.setAttribute("role", "option");
      item.textContent = option.textContent;
      item.addEventListener("click", () => commit(index), { signal });
      list.append(item);
      return item;
    });
    wrapper.append(button, list);
    select.after(wrapper);
    select.hidden = true;
    let active = select.selectedIndex;
    const highlight = (scroll = true) => {
      items.forEach((item, i) => item.classList.toggle("is-active", i === active));
      button.setAttribute("aria-activedescendant", items[active].id);
      if (scroll) items[active].scrollIntoView({ block: "nearest" });
    };
    const setOpen = open => {
      const wasOpen = button.getAttribute("aria-expanded") === "true";
      if (open) {
        clearTimeout(this.dropdownSelectionScrollTimer);
        sync();
      }
      wrapper.classList.toggle("is-open", open);
      button.setAttribute("aria-expanded", String(open));
      list.inert = !open;
      list.setAttribute("aria-hidden", String(!open));
      if (open) {
        active = items.findIndex(item => !item.hidden);
        highlight(false);
        list.style.setProperty("--rr-options-height", `${list.scrollHeight}px`);
        this.scrollDropdownTo(Math.max(0, window.scrollY + (label || button).getBoundingClientRect().top - this.scrollClearance().top));
      }
      else { button.removeAttribute("aria-activedescendant"); if (wasOpen) cancelAnimationFrame(this.dropdownScrollFrame); }
    };
    const sync = () => {
      caption.textContent = select.selectedOptions[0]?.textContent || "";
      items.forEach((item, i) => {
        // Keep closing rows in flow; refresh exclusions before reopening.
        if (!wrapper.classList.contains("is-open")) item.hidden = i === 0 || i === select.selectedIndex;
        item.setAttribute("aria-selected", String(i === select.selectedIndex));
      });
    };
    const scrollAfterSelection = () => {
      if (select === this.service) {
        this.scrollDropdownTo(() => Math.max(0, window.scrollY + (label || button).getBoundingClientRect().top - this.scrollClearance().top));
      } else {
        this.scrollToFormBottom();
      }
    };
    const commit = index => {
      select.selectedIndex = index;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      setOpen(false);
      button.focus({ preventScroll: true });
      clearTimeout(this.dropdownSelectionScrollTimer);
      scrollAfterSelection();
    };
    button.addEventListener("click", () => {
      const opening = button.getAttribute("aria-expanded") !== "true";
      setOpen(opening);
      if (!opening) scrollAfterSelection();
    }, { signal });
    button.addEventListener("keydown", event => {
      const open = button.getAttribute("aria-expanded") === "true";
      if (event.key === "Tab" || event.key === "Escape") { setOpen(false); return; }
      if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        event.preventDefault();
        if (!open) setOpen(true);
        else {
          const visible = items.map((item, i) => item.hidden ? -1 : i).filter(i => i >= 0);
          const position = visible.indexOf(active);
          active = event.key === "Home" ? visible[0] : event.key === "End" ? visible.at(-1) : visible[Math.max(0, Math.min(visible.length - 1, position + (event.key === "ArrowDown" ? 1 : -1)))];
          highlight();
        }
      } else if (open && ["Enter", " "].includes(event.key)) {
        event.preventDefault(); commit(active);
      } else if (event.key.length === 1 && event.key !== " " && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        if (!open) setOpen(true);
        const match = items.findIndex((item, i) => !item.hidden && i > active && item.textContent.toLowerCase().startsWith(event.key.toLowerCase()));
        const fallback = items.findIndex(item => !item.hidden && item.textContent.toLowerCase().startsWith(event.key.toLowerCase()));
        if (match >= 0 || fallback >= 0) { active = match >= 0 ? match : fallback; highlight(); }
      }
    }, { signal });
    document.addEventListener("pointerdown", event => { if (!wrapper.contains(event.target)) setOpen(false); }, { signal });
    for (const type of ["wheel", "touchstart", "keydown"]) {
      window.addEventListener(type, () => {
        cancelAnimationFrame(this.dropdownScrollFrame);
        clearTimeout(this.dropdownSelectionScrollTimer);
      }, { signal, passive: true, capture: true });
    }
    select.addEventListener("change", sync, { signal });
    setOpen(false);
    sync();
    return button;
  }

  scrollClearance() {
    const edge = pseudo => {
      const style = getComputedStyle(document.body, pseudo);
      return style.content !== "none" && style.position === "fixed" ? parseFloat(style.height) || 0 : 0;
    };
    return { top: edge("::after") + 16, bottom: edge("::before") + 16 };
  }

  scrollToFormBottom() {
    this.scrollDropdownTo(() => Math.max(0, window.scrollY + this.getBoundingClientRect().bottom - window.innerHeight + this.scrollClearance().bottom));
  }

  scrollDropdownTo(top, duration = 1000) {
    cancelAnimationFrame(this.dropdownScrollFrame);
    const start = window.scrollY;
    const destination = () => Math.min(typeof top === "function" ? top() : top, Math.max(0, document.documentElement.scrollHeight - window.innerHeight));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      window.scrollTo({ top: destination(), behavior: "instant" });
      return;
    }
    if (location.pathname === "/pages/concierge" && matchMedia("(max-width: 699px)").matches) {
      duration = Math.max(duration, Math.abs(destination() - start) / 0.28);
    }
    const began = performance.now();
    const step = now => {
      if (!this.isConnected) return;
      const progress = Math.min(1, (now - began) / duration);
      const eased = progress * progress * progress * (progress * (progress * 6 - 15) + 10);
      window.scrollTo({ top: start + (destination() - start) * eased, behavior: "instant" });
      if (progress < 1) this.dropdownScrollFrame = requestAnimationFrame(step);
    };
    this.dropdownScrollFrame = requestAnimationFrame(step);
  }

  resizeMessage() {
    const message = this.message;
    if (!message?.isConnected || (this.service && !this.service.value)) return;
    const currentHeight = message.getBoundingClientRect().height;
    this.messageResizeAnimation?.cancel();
    cancelAnimationFrame(this.messageScrollFrame);
    if (!this.messageMinimumHeight) {
      this.messageMinimumHeight = currentHeight;
    }
    message.style.minHeight = `${this.messageMinimumHeight}px`;
    const styles = getComputedStyle(message);
    const measure = message.cloneNode(false);
    measure.removeAttribute("id");
    measure.removeAttribute("name");
    measure.setAttribute("aria-hidden", "true");
    measure.tabIndex = -1;
    measure.value = message.value;
    measure.style.cssText = `position:fixed;visibility:hidden;pointer-events:none;left:-10000px;top:0;width:${message.getBoundingClientRect().width}px;height:0;min-height:0;transition:none;`;
    message.parentElement.append(measure);
    const borders = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
    const targetHeight = Math.max(this.messageMinimumHeight, Math.ceil(measure.scrollHeight + borders));
    measure.remove();
    message.style.height = `${targetHeight}px`;
    message.scrollTop = 0;
    const messageBounds = message.getBoundingClientRect();
    if (Math.abs(targetHeight - currentHeight) > 0.5 && messageBounds.top < window.innerHeight && messageBounds.bottom > 0 &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.messageResizeAnimation = message.animate([
        { height: `${currentHeight}px` },
        { height: `${targetHeight}px` }
      ], { duration: 300, easing: "ease-in-out" });
      const animation = this.messageResizeAnimation;
      const keepTextStationary = () => {
        message.scrollTop = 0;
        if (animation.playState === "running") {
          this.messageScrollFrame = requestAnimationFrame(keepTextStationary);
        }
      };
      this.messageScrollFrame = requestAnimationFrame(keepTextStationary);
    }
  }

  disconnectedCallback() {
    this.finishSketchScroll?.();
    clearTimeout(this.dropdownSelectionScrollTimer);
    cancelAnimationFrame(this.dropdownScrollFrame);
    cancelAnimationFrame(this.successAnchorFrame);
    this.sourceSwapAnimation?.cancel();
    cancelAnimationFrame(this.messageScrollFrame);
    this.serviceCopyAnimation?.cancel();
    this.messageResizeAnimation?.cancel();
    this.messageObserver?.disconnect();
    this.messageObserver = null;
    super.disconnectedCallback();
  }

  open() {
    if (!this.panel) return;
    this.frameState = "open";
    this.classList.add("is-open");
    this.panel.hidden = false;
    this.panel.removeAttribute("inert");
    this.panel.setAttribute("aria-hidden", "false");
  }

  close() {}
  scheduleOpenTitleMorphSync() {}
  announceAnchorLayoutSettled() {}

  async syncSource() {
    if (!this.source || !this.detail) return;
    this.syncOptionalLabels();
    if (this.activeSource) this.detailValues.set(this.activeSource, this.detail.value);
    const selected = this.source.selectedOptions[0];
    const key = selected?.dataset.sourceKey || "";
    const label = selected?.dataset.detailLabel || "";
    const previousLabel = this.detailLabel.textContent;
    const swapTarget = this.detailReveal.firstElementChild;
    const revision = this.sourceSwapRevision = (this.sourceSwapRevision || 0) + 1;
    this.sourceSwapAnimation?.cancel();
    const animateSwap = Boolean(label && previousLabel && label !== previousLabel &&
      this.activeSource && !this.detail.disabled && swapTarget?.animate &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    if (animateSwap) {
      this.sourceSwapAnimation = swapTarget.animate([{ opacity: 1 }, { opacity: 0 }],
        { duration: 180, easing: "ease", fill: "forwards" });
      try { await this.sourceSwapAnimation.finished; } catch {}
      if (revision !== this.sourceSwapRevision || !this.isConnected) return;
    }
    const changed = key !== this.activeSource;
    this.activeSource = key;
    if (changed) this.detail.value = this.detailValues.get(key) || "";
    this.detail.disabled = !label;
    this.detailReveal.inert = !label;
    this.detailReveal.setAttribute("aria-hidden", String(!label));
    this.detailReveal.classList.toggle("is-visible", Boolean(label));
    if (label) this.detailLabel.textContent = label;
    this.syncOptionalLabels();
    this.sourceSwapAnimation?.cancel();
    if (animateSwap) {
      this.sourceSwapAnimation = swapTarget.animate([{ opacity: 0 }, { opacity: 1 }],
        { duration: 220, easing: "ease" });
    }
  }

  async syncService() {
    if (!this.service || !this.messageReveal || !this.message) return;
    const visible = Boolean(this.service.value);
    const selected = this.service.selectedOptions?.[0];
    const label = this.querySelector("[data-page-message-label]");
    const hint = this.querySelector("[data-page-message-hint]");
    const copy = this.querySelector("[data-page-message-copy]");
    const revision = this.serviceCopyRevision = (this.serviceCopyRevision || 0) + 1;
    this.serviceCopyAnimation?.cancel();
    const animateSwap = Boolean(visible && this.activeService && !this.message.disabled && copy?.animate &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    if (animateSwap) {
      this.serviceCopyAnimation = copy.animate([{ opacity: 1 }, { opacity: 0 }],
        { duration: 180, easing: "ease", fill: "forwards" });
      try { await this.serviceCopyAnimation.finished; } catch {}
      if (revision !== this.serviceCopyRevision || !this.isConnected) return;
    }
    if (visible && selected) {
      if (label) label.textContent = selected.dataset.messageLabel || "";
      if (hint) {
        hint.textContent = selected.dataset.messageHint || "";
        hint.hidden = !hint.textContent;
      }
    }
    this.activeService = this.service.value;
    this.message.disabled = !visible;
    this.messageReveal.inert = !visible;
    this.messageReveal.setAttribute("aria-hidden", String(!visible));
    this.messageReveal.classList.toggle("is-visible", visible);
    this.serviceCopyAnimation?.cancel();
    if (animateSwap) {
      this.serviceCopyAnimation = copy.animate([{ opacity: 0 }, { opacity: 1 }],
        { duration: 220, easing: "ease" });
    }
    if (visible) this.resizeMessage();
  }

  syncOptionalLabels() {
    [this.source, this.detail].forEach(field => {
      if (!field) return;
      const label = this.querySelector(`label[for="${field.id}"], label[for="${field.id}-button"]`);
      const optional = label?.querySelector(".rr-page-inquiry__optional");
      if (optional) {
        const populated = Boolean(field.value.trim());
        optional.classList.toggle("is-populated", populated);
        optional.setAttribute("aria-hidden", String(populated));
      }
    });
  }

  syncNewsletterOption({ animate = true } = {}) {
    if (!this.newsletterOption || !this.newsletterOptIn) return;
    const visible = this.emailLooksComplete(this.emailInput?.value.trim() || "") && Boolean(this.customerEndpointUrl);
    const checked = visible ? this.newsletterConsentChoice ?? this.detectedCountryCode === "US" : false;
    this.setNewsletterOptionState(visible, checked);
    this.syncPrivacyCopy({ animate, resizeFrame: false, preserveFrameHeight: true });
  }

  syncPrivacyCopy(options = {}) {
    super.syncPrivacyCopy({ ...options, resizeFrame: false, preserveFrameHeight: true });
  }

  getContactValidationError() {
    const error = super.getContactValidationError();
    if (error) return error;
    if (this.service && !this.service.value) {
      const button = this.service.nextElementSibling?.querySelector("button");
      return { message: "Please select how we may assist you.", focusTarget: button || this.service, invalidFields: [button || this.service] };
    }
    const message = this.querySelector("[data-page-message]");
    if (message && !message.value.trim()) {
      return { message: this.dataset.messageError, focusTarget: message, invalidFields: [message] };
    }
    return null;
  }

  validateAndPrepareSubmission() {
    const valid = super.validateAndPrepareSubmission();
    if (valid) this.saveDraft();
    return valid;
  }

  handleSubmission(event) {
    if (this.submissionPending) {
      event.preventDefault();
      return;
    }
    super.handleSubmission(event);
  }

  get currentInquiryCustomer() {
    const email = this.newsletterEmail;
    const phone = this.newsletterPhone;
    const firstName = this.firstNameInput?.value.trim();
    const lastName = this.lastNameInput?.value.trim();
    if (!firstName || !lastName || (!email && !phone)) return null;
    return {
      email, phone, firstName, lastName, marketingConsent: this.shouldSubscribeToNewsletter,
      legacyNewsletter: true, expiresAt: Date.now() + 1800000,
      leadId: this.submissionLeadId,
      submittedAt: this.submissionDateTimeTransport?.value.trim() || new Date().toISOString(),
      productName: location.pathname === "/pages/concierge" ? "Concierge Inquiry" : "General Inquiry",
      inquiryType: location.pathname === "/pages/concierge" ? "concierge" : "general",
      productSku: "", productPrice: "Not applicable", productUrl: location.origin + location.pathname,
      attribution: window.RrInquiryAttribution?.get() || null,
      test: window.Shopify?.theme?.role === "unpublished" || window.Shopify?.designMode === true
    };
  }

  storePendingCustomerSync(value = this.currentInquiryCustomer) {
    if (!value) return false;
    try {
      this.pendingCustomerStorage?.setItem(this.pendingCustomerStorageKey, JSON.stringify(value));
      return true;
    } catch { return false; }
  }

  readPendingCustomerSync() {
    try {
      const value = JSON.parse(this.pendingCustomerStorage?.getItem(this.pendingCustomerStorageKey) || "null");
      if (!value || !Number.isFinite(value.expiresAt) || value.expiresAt < Date.now() || value.legacyNewsletter !== true ||
          typeof value.firstName !== "string" || typeof value.lastName !== "string" ||
          typeof value.marketingConsent !== "boolean" ||
          (value.email && !this.emailLooksComplete(value.email)) ||
          (value.phone && !/^\+[1-9]\d{6,14}$/.test(value.phone)) ||
          (!value.email && !value.phone)) return null;
      return value;
    } catch { return null; }
  }

  get draftKey() { return `rr:page-inquiry:draft:${this.panel?.id || ""}`; }

  saveDraft() {
    const fields = {};
    this.form.querySelectorAll("input:not([type=hidden]), textarea:not([hidden]), select").forEach(field => {
      if (field.id) fields[field.id] = field.type === "checkbox" ? field.checked : field.value;
    });
    try {
      this.pendingCustomerStorage?.setItem(this.draftKey, JSON.stringify({ expiresAt: Date.now() + 1800000, fields }));
    } catch {}
  }

  restoreDraft() {
    if (this.querySelector("[data-inquiry-success]")) return;
    try {
      const draft = JSON.parse(this.pendingCustomerStorage?.getItem(this.draftKey) || "null");
      if (!draft || !Number.isFinite(draft.expiresAt) || draft.expiresAt < Date.now()) {
        this.pendingCustomerStorage?.removeItem(this.draftKey);
        return;
      }
      this.form.querySelectorAll("input, textarea, select").forEach(field => {
        const value = draft.fields?.[field.id];
        if (field.type === "checkbox" && typeof value === "boolean") {
          field.checked = value;
          if (field === this.newsletterOptIn) this.newsletterConsentChoice = value;
        } else if (typeof value === "string") field.value = value;
      });
      this.activeSource = this.source?.selectedOptions[0]?.dataset.sourceKey || "";
      if (this.activeSource) this.detailValues.set(this.activeSource, this.detail.value);
      this.refreshPhonePresentation();
    } catch {}
  }

  responseAcceptedWithoutMarker() { return false; }

  conciergeAnchorTop() {
    const section = this.closest(".shopify-section--contact");
    const hand = !this.querySelector("[data-inquiry-success]") && location.pathname === "/pages/concierge" && matchMedia("(max-width: 699px)").matches
      ? section?.querySelector(".rr-concierge-contact__image") : null;
    const anchor = hand || section?.querySelector(".section-header") || this;
    const menu = hand && document.querySelector('header [aria-controls="sidebar-menu"], header [aria-label="Navigation menu"]');
    const clearance = menu ? menu.getBoundingClientRect().bottom + 8 - hand.querySelector("img").getBoundingClientRect().height * (73 / 1600) : this.scrollClearance().top;
    return Math.max(0, window.scrollY + anchor.getBoundingClientRect().top - clearance);
  }

  scrollToSuccessAnchor(slower = false, duration = 2000, instant = false) {
    cancelAnimationFrame(this.successAnchorFrame);
    this.successAnchorFrame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      if (slower) {
        this.scrollDropdownTo(() => this.conciergeAnchorTop(), duration);
        return;
      }
      window.scrollTo({
        top: this.conciergeAnchorTop(),
        behavior: instant || window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth"
      });
    });
  }

  transitionToSuccess(successContent) {
    try { this.pendingCustomerStorage?.removeItem(this.draftKey); } catch {}
    this.form.replaceChildren(successContent);
    this.form.setAttribute("aria-busy", "false");
    this.form.setAttribute("role", "status");
    const focus = this.form.querySelector("[data-inquiry-success-focus]");
    focus?.focus({ preventScroll: true });
    this.scrollToSuccessAnchor();
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      successContent.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: "ease-out", fill: "both" });
    }
  }
}

if (!customElements.get("rr-page-inquiry")) customElements.define("rr-page-inquiry", RRPageInquiry);
